import React, { useEffect, useState } from 'react'
import I18n from 'i18n'
import { MyAxios as axios } from '../../../../MyAxios'
import { WithQuestionnaires } from '../../../../questionnaires/QuestionnaireProvider'
import { readGenericQuestionnaire } from '../../../../common/utils/QuestionnaireUtils'
import Field from '../common/Field'

interface QuestionnaireData {
  questionnaire?: {
    key?: string
  }
}

interface QuestionnaireDefinition {
  content?: Record<string, unknown>
}

interface QuestionnaireField {
  id: string
  name: string
  value: string | number | null
}

interface Props {
  data: QuestionnaireData
  questionnaires?: {
    questionnaireDefinitionUrl: (questionnaireKey: string) => string
    authenticatedHeaders: () => Record<string, string>
  }
}

const MAX_VISIBLE_FIELDS = 4
const MAX_TITLE_LENGTH = 30

const truncate = (text: string, max: number = MAX_TITLE_LENGTH): string => {
  if (text == null) return text
  return text.length > max ? `${text.slice(0, max).trimEnd()}…` : text
}

const resolveTitle = (questionnaireKey: string | undefined, field: QuestionnaireField): string => {
  if (questionnaireKey != null && field.id != null) {
    const shortLabel = I18n.t(`questionnaires.${questionnaireKey}.${field.id}`, { defaultValue: '' })
    if (typeof shortLabel === 'string' && shortLabel !== '') return shortLabel
  }
  return truncate(field.name)
}

const RheumatismQuestionnaire: React.FC<Props> = ({ data, questionnaires }) => {
  const key = data?.questionnaire?.key
  const [questionnaireDefinition, setQuestionnaireDefinition] = useState<QuestionnaireDefinition>({})
  const [questionsAndAnswers, setQuestionsAndAnswers] = useState<QuestionnaireField[]>([])

  useEffect(() => {
    if (typeof key !== 'string' || key.length === 0) return

    axios({
      method: 'GET',
      url: questionnaires?.questionnaireDefinitionUrl(key),
      headers: { ...questionnaires?.authenticatedHeaders(), Accept: 'application/json' }
    }).then((response) => {
      setQuestionnaireDefinition(response.data)
    }).catch((err) => {
      console.log('Error retrieving rheumatism questionnaire definition')
      console.log(err)
    })
  }, [key, questionnaires])

  useEffect(() => {
    if (questionnaireDefinition.content != null) {
      setQuestionsAndAnswers(readGenericQuestionnaire(data, questionnaireDefinition, I18n.locale))
    }
  }, [data, questionnaireDefinition])

  const visibleFields = questionsAndAnswers.slice(0, MAX_VISIBLE_FIELDS)

  return (
    <>
      {visibleFields.length > 0 && (
        <div className='sport-fields'>
          {visibleFields.map(field => (
            <Field
              key={field.id}
              title={resolveTitle(key, field)}
              value={field.value}
            />
          ))}
        </div>
      )}
    </>
  )
}

export default WithQuestionnaires(RheumatismQuestionnaire)
